import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Trailers, } from '../interfaces';
import { DataServiceService } from '../services/data-service.service';
import { Table } from 'primeng/table';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';


@Component({
  selector: 'app-drivers',
  templateUrl: './drivers.component.html',
  styleUrl: './drivers.component.scss'
})


export class DriversComponent implements OnInit {

  constructor(private dataServiceService: DataServiceService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private authService: AuthService
  ) { }

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('drivertable') drivertable!: Table;

  drivers: any[] = [];
  selectedDriver: any = null;
  editingDriver: any = null;
  clonedDriver: any = null;

  trucks: any[] = [];
  trailers: Trailers[] = []
  isAdmin = false;
  hasAccess = false;
  types: { label: string, value: string }[] = [
    { label: 'Conveyor', value: 'Conveyor' },
    { label: 'Dump', value: 'Dump' },
    { label: 'Hopper', value: 'Hopper' },
    { label: 'Pneumatic', value: 'Pneumatic' }
  ];

  newDriverDialogVisible: boolean = false
  newDriver: any = {};
  saving: boolean = false;

  ngOnInit(): void {
    this.dataServiceService.getAllTrucks().subscribe(data => {
      this.trucks = data;
      this.trucks.sort((a: { truck_number: string; }, b: { truck_number: string; }) => a.truck_number.localeCompare(b.truck_number, undefined, {numeric: true}));
    });

    this.dataServiceService.getAllTrailers().subscribe(data => {
      this.trailers = data;
      this.trailers.sort((a: { trailer_number: string; }, b: { trailer_number: string; }) => a.trailer_number.localeCompare(b.trailer_number, undefined, {numeric: true}));
    });

    // Subscribe to drivers updates
    this.dataServiceService.drivers$.subscribe(drivers => {
      this.drivers = drivers;
    });

    // Initial load of drivers
    this.dataServiceService.getDriverDetails().subscribe();

    // Admin check
    this.authService.userProfile$.subscribe(user =>
      this.isAdmin = user?.roles?.includes('Admin') ?? false
    );

    this.dataServiceService.trucks$.subscribe(trucks => {
      this.trucks = trucks;
    })

    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });
  }

  newDriverDialog() {
    this.newDriverDialogVisible = true
  }


  saveNewDriver() {
    // Validate required fields
    if (!this.newDriver.first_name || !this.newDriver.last_name) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'First name and last name are required'
      });
      return;
    }

    this.saving = true;
    const driverToCreate = {
      first_name: this.newDriver.first_name,
      last_name: this.newDriver.last_name,
      truck_id: this.newDriver.truck_id || null,
      trailer_id: this.newDriver.trailer_id || null,
      type: this.newDriver.type || '',
      notes: this.newDriver.notes || ''
    };


    this.dataServiceService.createDriver(driverToCreate).subscribe({
      next: () => {
        this.saving = false;
        this.newDriverDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Driver added successfully'
        });

        // Refresh the drivers data
        this.dataServiceService.getDriverDetails().subscribe();
        this.resetNewDriverForm();
      },
      error: (error) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add driver'
        });
        console.error('Add error:', error);
      }
    });
  }

  clear(table: Table) {
    table.clear();
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
  }

  onRowEditInit(driver: any) {
    // Store the original driver data
    this.clonedDriver = { ...driver };
    this.editingDriver = driver;
  }

  onRowEditSave(driver: any) {
    if (driver) {

      const updatedDriver = {
        ...driver,
        truck_id: driver.truck_id ?? null,
        trailer_id: driver.trailer_id ?? null,
        type: driver.type ?? null
      };

      this.dataServiceService.updateDriverDetails(driver.driver_id, updatedDriver).subscribe(
        () => {
          this.editingDriver = null;
          this.clonedDriver = null;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Driver updated successfully'
          });
        },
        error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update driver'
          });
          console.error('Update error:', error);
        }
      );
    }
  }

  onRowEditCancel(driver: any) {
    // Restore the original data
    if (this.clonedDriver) {
      Object.assign(driver, this.clonedDriver);
    }
    this.editingDriver = null;
    this.clonedDriver = null;
    this.resetNewDriverForm()
  }


  onTruckChange(event: any, driver: any) {
    const rawValue = event?.value;
    const selectedTruckId: number | null = typeof rawValue === 'object' && rawValue !== null ? rawValue.truck_id : rawValue ?? null;
    if (selectedTruckId) {
      const selectedTruck = typeof rawValue === 'object' && rawValue !== null
        ? rawValue
        : this.trucks.find(t => t.truck_id === selectedTruckId);
      if (!selectedTruck) {
        return;
      }
      // Check if truck is assigned to another driver
      const existingAssignment = this.drivers.find(d => {
        const matchesById = d.truck_id != null && d.truck_id === selectedTruck.truck_id;
        const matchesByNumber = d.truck_number && d.truck_number === selectedTruck.truck_number;
        return (matchesById || matchesByNumber) && d.driver_id !== driver.driver_id;
      });

      if (existingAssignment) {
        this.confirmationService.confirm({
          message: `This truck is currently assigned to ${existingAssignment.first_name} ${existingAssignment.last_name}. Would you like to reassign it?`,
          acceptLabel: 'Yes',
          rejectLabel: 'No',
          acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
          rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
          accept: () => {
            // Remove truck from existing driver
            this.dataServiceService.updateDriverDetails(existingAssignment.driver_id, {
              ...existingAssignment,
              truck_number: null,
              truck_id: null
            }).subscribe(
              () => {
                // Assign truck to new driver
                driver.truck_number = selectedTruck.truck_number;
                driver.truck_id = selectedTruck.truck_id;

                // Update the drivers array to reflect changes
                const index = this.drivers.findIndex(d => d.driver_id === existingAssignment.driver_id);
                if (index !== -1) {
                  this.drivers[index].truck_number = null;
                  this.drivers[index].truck_id = null;
                }

                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Truck reassigned successfully'
                });
              },
              error => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Failed to reassign truck'
                });
                console.error('Reassignment error:', error);
              }
            );
          },
          reject: () => {
            // Reset the dropdown to its previous value
            if (driver.driver_id) {
              driver.truck_number = this.clonedDriver?.truck_number || null;
              driver.truck_id = this.clonedDriver?.truck_id || null;
            } else {
              // New driver form case
              driver.truck_number = null;
              driver.truck_id = null;
            }
          }
        });
      } else {
        // No conflict, proceed with normal assignment
        driver.truck_number = selectedTruck.truck_number;
        driver.truck_id = selectedTruck.truck_id;
      }
    } else {
      driver.truck_number = null;
      driver.truck_id = null;
    }
  }



  onTrailerChange(event: any, driver: any) {
    const rawValue = event?.value;
    const selectedTrailerId: number | null = typeof rawValue === 'object' && rawValue !== null ? rawValue.trailer_id : rawValue ?? null;
    if (selectedTrailerId) {
      const selectedTrailer = typeof rawValue === 'object' && rawValue !== null
        ? rawValue
        : this.trailers.find(t => t.trailer_id === selectedTrailerId);
      if (!selectedTrailer) {
        return;
      }
      // Check if trailer is assigned to another driver
      const existingAssignment = this.drivers.find(d => {
        const matchesById = d.trailer_id != null && d.trailer_id === selectedTrailer.trailer_id;
        const matchesByNumber = d.trailer_number && d.trailer_number === (selectedTrailer as any).trailer_number;
        return (matchesById || matchesByNumber) && d.driver_id !== driver.driver_id;
      });

      if (existingAssignment) {
        this.confirmationService.confirm({
          message: `This trailer is currently assigned to ${existingAssignment.first_name} ${existingAssignment.last_name}. Would you like to reassign it?`,
          acceptLabel: 'Yes',
          rejectLabel: 'No',
          acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
          rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
          accept: () => {
            // Remove trailer from existing driver
            this.dataServiceService.updateDriverDetails(existingAssignment.driver_id, {
              ...existingAssignment,
              trailer_number: null,
              trailer_id: null
            }).subscribe(
              () => {
                // Assign trailer to new driver
                driver.trailer_number = (selectedTrailer as any).trailer_number;
                driver.trailer_id = selectedTrailer.trailer_id;

                // Update the drivers array to reflect changes
                const index = this.drivers.findIndex(d => d.driver_id === existingAssignment.driver_id);
                if (index !== -1) {
                  this.drivers[index].trailer_number = null;
                  this.drivers[index].trailer_id = null;
                }

                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Trailer reassigned successfully'
                });
              },
              error => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Failed to reassign trailer'
                });
                console.error('Reassignment error:', error);
              }
            );
          },
          reject: () => {
            // Reset the dropdown to its previous value
            if (driver.driver_id) {
              driver.trailer_number = this.clonedDriver?.trailer_number || null;
              driver.trailer_id = this.clonedDriver?.trailer_id || null;
            } else {
              // New driver form case
              driver.trailer_number = null;
              driver.trailer_id = null;
            }
          }
        });
      } else {
        // No conflict, proceed with normal assignment
        driver.trailer_number = (selectedTrailer as any).trailer_number;
        driver.trailer_id = selectedTrailer.trailer_id;
      }
    } else {
      driver.trailer_number = null;
      driver.trailer_id = null;
    }
  }


  onNotesChange(event: any, driver: any) {
    driver.notes = event.value.notes
  }


  removeDriver(driver: any) {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove ${driver.first_name} ${driver.last_name}?`,
      header: 'Confirm Removal',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        this.dataServiceService.removeDriver(driver.driver_id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Driver removed successfully'
            });
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to remove driver'
            });
            console.error('Remove error:', error);
          }
        });
      }
    });
  };


  resetNewDriverForm() {
    this.newDriver = {
      first_name: '',
      last_name: '',
      truck_id: null,
      truck_number: null,
      trailer_id: null,
      trailer_number: null,
      type: null,
      notes: ''
    };
    this.newDriverDialogVisible = false
  };

  // Utility to get only spare trucks (not assigned to any driver)
  getSpareTrucks(): any[] {
    const assignedTruckIds = new Set(this.drivers.filter(d => d.truck_id != null).map(d => d.truck_id));
    return this.trucks.filter(t => !assignedTruckIds.has(t.truck_id));
  }


  showDialog(driver: any) {
    this.router.navigate(['/driver-schedule', driver.driver_id])
  };
}
